// Post-login "return to" destination (e.g. sign-in FROM checkout should land you back at
// /checkout with your basket, not the dashboard). Carried in sessionStorage so it survives the
// OTP step and mid-navigation URL changes.
//
// Two things read this: the Login page (imperative navigate on success) and the GuestRoute guard
// (declarative <Navigate> when auth flips). They MUST agree, so both go through readAuthReturn()
// here. Readers are pure — they never write/clear during render (clearing mid-render can double-
// read to empty and bounce to the dashboard, the original bug). The destination page clears the
// flag on arrival via clearAuthReturn().
//
// Belt-and-braces: the flag is stamped with a timestamp and IGNORED once older than TTL_MS. So a
// stale flag from an ABANDONED checkout sign-in (user clicked "Sign in" from checkout, wandered
// off, then logged in normally later) simply expires instead of wrongly bouncing them to checkout.
// An expired flag is left in place (harmless once ignored) and gets overwritten by the next real
// checkout sign-in or cleared on the next checkout arrival.

const KEY = 'auth_return_to'
const TS_KEY = 'auth_return_to_ts'
const TTL_MS = 15 * 60 * 1000 // 15 minutes — long enough to sign in (incl. OTP), short enough to go stale

// Internal same-origin paths only ("/foo", never "//evil.com" or absolute URLs).
const isSafePath = (v: string | null): v is string =>
  !!v && v.startsWith('/') && !v.startsWith('//')

/** Stash where to return after login (called by the checkout gate before navigating to /login). */
export function setAuthReturn(path: string): void {
  if (typeof window === 'undefined' || !isSafePath(path)) return
  try {
    sessionStorage.setItem(KEY, path)
    sessionStorage.setItem(TS_KEY, String(Date.now()))
  } catch { /* ignore (private mode / quota) */ }
}

/**
 * The pending post-login destination, or null if absent, unsafe, or expired.
 * PURE — never writes or clears (safe to call during render).
 */
export function readAuthReturn(): string | null {
  if (typeof window === 'undefined') return null
  let raw: string | null = null
  let ts: string | null = null
  try {
    raw = sessionStorage.getItem(KEY)
    ts = sessionStorage.getItem(TS_KEY)
  } catch { return null }
  if (!isSafePath(raw)) return null
  const ms = ts ? parseInt(ts, 10) : NaN
  if (!Number.isFinite(ms) || Date.now() - ms > TTL_MS) return null // stale/abandoned → ignore
  return raw
}

/** Consume the flag once the destination is reached (called by checkout on signed-in arrival). */
export function clearAuthReturn(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(KEY)
    sessionStorage.removeItem(TS_KEY)
  } catch { /* ignore */ }
}
